import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import WebhookModal from '../components/WebhookModal';
import { useAuth } from '../contexts/AuthContext';
import ServerDataTable from '../components/ServerDataTable/ServerDataTable';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/formatDate';

// Create a function to get the validation schema based on whether we're editing or creating
const getValidationSchema = (isEditing) => {
  return Yup.object({
    storeName: Yup.string(),
    storeUrl: Yup.string()
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/,
        'Must be a valid Shopify store URL (e.g., store-name.myshopify.com)'
      )
      .required('Store URL is required'),
    accessToken: isEditing
      ? Yup.string().optional() // Optional when editing
      : Yup.string().required('Access token is required'),
    webhookSecret: isEditing
      ? Yup.string().optional() // Optional when editing
      : Yup.string().required('Webhook secret is required'),
    bssB2bAccessKey: isEditing
      ? Yup.string().optional() // Optional when editing
      : Yup.string().required('BSS B2B Access Key is required'),
    bssB2bApiUrl: Yup.string()
      .url('Must be a valid URL')
      .required('BSS B2B API URL is required')
  });
};

export default function ShopifyConfig() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [testingConnectionId, setTestingConnectionId] = useState(null);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [showWebhookResult, setShowWebhookResult] = useState(false);
  const [originalFormValues, setOriginalFormValues] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const [loadingRows, setLoadingRows] = useState([]);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [showBssB2bAccessKey, setShowBssB2bAccessKey] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Columns
  const columns = [
    {
      field: 'storeName',
      headerName: 'Store Name',
      width: 150,
      align: 'left',
      sortField: 'store_name' // Map to database field name
    },
    {
      field: 'storeUrl',
      headerName: 'Store URL',
      width: 150,
      sortField: 'store_url' // Map to database field name
    },
    {
      field: 'webhookVersion',
      headerName: 'Version',
      width: 150,
      sortField: 'webhook_version' // Map to database field name
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 150,
      align: 'left',
      sortField: 'is_active', // Map to database field name
      renderCell: ({ value }) => (
        <StatusBadge status={value ? 'Active' : 'Inactive'} />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 200,
      sortField: 'created_at', // Map to database field name
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 200,
      sortField: 'updated_at', // Map to database field name
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'right',
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <DeleteIcon />
            </IconButton>
            {!row.isActive && (
              <IconButton size="small" color="success" onClick={() => handleSetActive(row)}>
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }
    }
  ];

  // Fetch active Shopify configuration
  const { data: activeConfig, isLoading: isLoadingActive } = useQuery({
    queryKey: ['shopifyConfig', 'active'],
    queryFn: async () => {
      try {
        const response = await api.get('/shopify-config/active');
        // Handle the standard API response format: { status: "success", data: config }
        if (response.data && response.data.status === 'success' && response.data.data) {
          return response.data.data;
        }
        // Fallback for direct data response
        else if (response.data && !response.data.status) {
          return response.data;
        }

        return null;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });

  // Fetch all Shopify configurations (admin only)
  const { data: allConfigs, isLoading: isLoadingAll } = useQuery({
    queryKey: ['shopifyConfig', 'all', pageIndex, pageSize],
    queryFn: async () => {
      if (!isAdmin) return { data: [], pagination: { total: 0, totalPages: 0 } };
      try {
        const params = new URLSearchParams({
          page: pageIndex + 1,
          pageSize: pageSize.toString()
        });

        const response = await api.get(`/shopify-config?${params.toString()}`);

        // Handle the standard API response format: { status: "success", data: configs, pagination: {...} }
        if (response.data && response.data.status === 'success') {
          return {
            data: response.data.data || [],
            pagination: response.data.pagination || { total: 0, totalPages: 0 }
          };
        }

        // Fallback for direct data response
        else if (Array.isArray(response.data)) {
          return {
            data: response.data,
            pagination: { total: response.data.length, totalPages: 1 }
          };
        }

        return { data: [], pagination: { total: 0, totalPages: 0 } };
      } catch (error) {
        console.error('Error fetching Shopify configs:', error);
        return { data: [], pagination: { total: 0, totalPages: 0 } };
      }
    },
    enabled: isAdmin,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });

  // Create configuration mutation
  const createMutation = useMutation({
    mutationFn: async (configData) => {
      const createData = { ...configData };
      // Set new configurations as inactive by default
      createData.isActive = false;
      Object.keys(createData).forEach(key => {
        if (createData[key] === '') {
          delete createData[key];
        }
      });
      const response = await api.post('/shopify-config', createData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig'] });
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'shopify', 'active'] });
      setIsDialogOpen(false);
      setHasFormChanges(false);
      toast.success('Shopify configuration created successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unknown error occurred';
      toast.error(`Failed to create Shopify configuration: ${errorMessage}`);
    }
  });

  // Update configuration mutation
  const updateMutation = useMutation({
    mutationFn: async (configData) => {
      const updateData = { ...configData };

      // Remove empty fields that should not be updated
      if (!updateData.accessToken) delete updateData.accessToken;

      // Don't update BSS B2B Access Key if empty (keep the old value)
      if (!updateData.bssB2bAccessKey || updateData.bssB2bAccessKey.trim() === '') {
        delete updateData.bssB2bAccessKey;
      }

      const response = await api.put(`/shopify-config/${configData.id}`, updateData);
      return response.data;
    },
    onMutate: async (configData) => {
      // Add config ID to loading rows
      setLoadingRows(prev => [...prev, configData.id]);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig'] });
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'shopify', 'active'] });
      setIsDialogOpen(false);
      setHasFormChanges(false);
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(id => id !== data.data?.id));
      toast.success('Shopify configuration updated successfully');
    },
    onError: (error, configData) => {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unknown error occurred';
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(id => id !== configData.id));
      toast.error(`Failed to update Shopify configuration: ${errorMessage}`);
    }
  });

  // Delete configuration mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/shopify-config/${id}`);
      return response.data;
    },
    onMutate: async (id) => {
      // Add config ID to loading rows
      setLoadingRows(prev => [...prev, id]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig'] });
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'shopify', 'active'] });
      setIsDeleteDialogOpen(false);
      // Remove all loading rows since data will refetch
      setLoadingRows([]);
      toast.success('Shopify configuration deleted successfully');
    },
    onError: (error, id) => {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unknown error occurred';
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(loadingId => loadingId !== id));
      toast.error(`Failed to delete Shopify configuration: ${errorMessage}`);
    }
  });

  // Set active configuration mutation
  const setActiveMutation = useMutation({
    mutationFn: async (id) => {
      // First, get the configuration to validate it
      const configToActivate = allConfigs?.data?.find(config => config.id === id);

      if (!configToActivate) {
        throw new Error('Configuration not found');
      }

      // Validate the configuration has a store URL
      const storeUrl = configToActivate.storeUrl || configToActivate.store_url || '';

      if (!storeUrl || storeUrl.trim() === '') {
        throw new Error('Store URL is required to set a configuration as active');
      }

      // Update the configuration to set isActive to true
      // Include all required fields from the configuration
      const updateData = {
        isActive: true,
        storeUrl
      };

      // Include store name if it exists
      if (configToActivate.storeName || configToActivate.store_name) {
        updateData.storeName = configToActivate.storeName || configToActivate.store_name;
      }

      const response = await api.put(`/shopify-config/${id}`, updateData);

      // If successful, set all other configurations to inactive
      if (response.data) {
        // Get all other configurations
        const otherConfigs = allConfigs?.data?.filter(config => config.id !== id) || [];

        // Update each one to be inactive
        for (const config of otherConfigs) {
          if (config.isActive || config.is_active) {
            // Include all required fields when deactivating
            const deactivateData = {
              isActive: false,
              storeUrl: config.storeUrl || config.store_url
            };

            // Include store name if it exists
            if (config.storeName || config.store_name) {
              deactivateData.storeName = config.storeName || config.store_name;
            }

            await api.put(`/shopify-config/${config.id}`, deactivateData);
          }
        }
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig'] });
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'shopify', 'active'] });
      toast.success('Configuration set as active successfully');
    },
    onError: (error) => {
      console.error('Set active configuration error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to set active configuration: ${errorMessage}`);
    }
  });

  // Deactivate configuration mutation
  const deactivateMutation = useMutation({
    mutationFn: async (id) => {
      // Get the configuration to deactivate
      const configToDeactivate = allConfigs?.data?.find(config => config.id === id);

      if (!configToDeactivate) {
        throw new Error('Configuration not found');
      }

      // Update the configuration to set isActive to false
      const updateData = {
        isActive: false,
        storeUrl: configToDeactivate.storeUrl || configToDeactivate.store_url
      };

      // Include store name if it exists
      if (configToDeactivate.storeName || configToDeactivate.store_name) {
        updateData.storeName = configToDeactivate.storeName || configToDeactivate.store_name;
      }

      const response = await api.put(`/shopify-config/${id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig'] });
      queryClient.invalidateQueries({ queryKey: ['shopifyConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'shopify', 'active'] });
      setIsDeactivateDialogOpen(false);
      setSelectedConfig(null);
      toast.success('Configuration deactivated successfully');
    },
    onError: (error) => {
      console.error('Deactivate configuration error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to deactivate configuration: ${errorMessage}`);
    }
  });

  // Test connection
  const handleTestConnection = async (id) => {
    setTestingConnectionId(id);

    try {
      if (!id && !activeConfig?.id) {
        throw new Error('No active Shopify configuration found. Please create a configuration first.');
      }

      const url = id ? `/shopify-config/${id}/test-connection` : `/shopify-config/${activeConfig.id}/test-connection`;
      const response = await api.post(url);

      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      toast.success(response.data.message || 'Connection test successful');
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to connect to Shopify';

      let userMessage = 'Connection test failed: ';

      if (errorMessage.includes('not found') || errorMessage.includes('No active')) {
        userMessage += 'Shopify configuration not found. Please check your configuration details.';
      } else if (errorMessage.includes('Invalid access token') || errorMessage.includes('unauthorized')) {
        userMessage += 'Invalid access token. Please update your Shopify credentials.';
      } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
        userMessage += 'Network error. Please check your internet connection.';
      } else {
        userMessage += errorMessage;
      }

      toast.error(userMessage);
    } finally {
      setTestingConnectionId(null);
    }
  };

  // Form handling with dynamic validation schema
  const formik = useFormik({
    initialValues: {
      id: '',
      storeName: '',
      storeUrl: '',
      accessToken: '',
      webhookSecret: '',
      bssB2bAccessKey: '',
      bssB2bApiUrl: ''
    },
    validate: (values) => {
      const isEditing = !!values.id; // Use values.id to determine if editing
      const schema = getValidationSchema(isEditing);
      try {
        schema.validateSync(values, { abortEarly: false });
        return {};
      } catch (err) {
        if (!err.inner) return {};
        return err.inner.reduce((errors, error) => {
          errors[error.path] = error.message;
          return errors;
        }, {});
      }
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values.id) {
        values.isActive = false;
        updateMutation.mutate(values);
      } else {
        createMutation.mutate(values);
      }
    }
  });

  // Track form changes
  useEffect(() => {
    if (originalFormValues) {
      const currentStringified = JSON.stringify(formik.values);
      const originalStringified = JSON.stringify(originalFormValues);
      setHasFormChanges(currentStringified !== originalStringified);
    }
  }, [formik.values, originalFormValues]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isDialogOpen) {
      formik.resetForm();
      setSelectedConfig(null);
      setOriginalFormValues(null);
      setHasFormChanges(false);
      // Reset password visibility states
      setShowAccessToken(false);
      setShowWebhookSecret(false);
      setShowBssB2bAccessKey(false);
    }
  }, [isDialogOpen]);

  // Handle edit button click
  const handleEdit = (config) => {
    setSelectedConfig(config);
    const configData = config.data ? config.data : config;

    const id = configData.id || '';
    const storeName = configData.storeName || configData.store_name || '';
    const storeUrl = configData.storeUrl || configData.store_url || '';
    const accessToken = configData.accessToken || configData.access_token || '';
    const bssB2bApiUrl = configData.bssB2bApiUrl || configData.bss_b2b_api_url || '';

    const formValues = {
      id,
      storeName,
      storeUrl,
      accessToken,
      webhookSecret: '',
      bssB2bAccessKey: '',
      bssB2bApiUrl
    };

    formik.setValues(formValues);
    setOriginalFormValues(formValues);
    setIsDialogOpen(true);
  };

  // Handle add button click
  const handleAdd = () => {
    setSelectedConfig(null);
    formik.resetForm();
    const formValues = {
      id: '',
      storeName: '',
      storeUrl: '',
      accessToken: '',
      webhookSecret: '',
      bssB2bAccessKey: '',
      bssB2bApiUrl: ''
    };
    formik.setValues(formValues);
    setOriginalFormValues(formValues);
    setIsDialogOpen(true);
  };

  // Handle delete button click
  const handleDelete = (config) => {
    setSelectedConfig(config);
    setIsDeleteDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setOriginalFormValues(null);
    setHasFormChanges(false);
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
  };

  // Handle set active button click
  const handleSetActive = async (config) => {
    try {
      setLoadingRows(prev => [...prev, config.id]);

      const response = await api.post(`/shopify-config/${config.id}/test-connection`);

      if (response.data && response.data.status === 'success') {
        toast.success(response.data.message || 'Connection test successful! Activating configuration...');

        const updatedConfig = { ...config, isActive: true };
        updateMutation.mutate(updatedConfig);
      } else {
        toast.error(response.data.message || 'Connection test failed. Cannot activate configuration.');
        setLoadingRows(prev => prev.filter(id => id !== config.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Connection test failed. Cannot activate configuration.');
      setLoadingRows(prev => prev.filter(id => id !== config.id));
    }
  };

  return (
    <Box>
      <PageHeader
        title="Shopify Configuration"
        subtitle="Manage your Shopify store connection settings"
      />
      {/* Active Configuration */}
      <Card sx={{ mb: 4, boxShadow: 10 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Active Configuration
            </Typography>
          </Box>

          {isLoadingActive ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Store Name
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Store URL
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Webhook Version
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    BSS B2B API URL
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Created At
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Updated At
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Status
                  </Typography>
                  <Skeleton variant="text" width="80%" height={29} />
                </Grid>
                {isAdmin && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <Skeleton variant="rectangular" width={180} height={41} sx={{ borderRadius: 1 }} />
                      <Skeleton variant="rectangular" width={80} height={41} sx={{ borderRadius: 1 }} />
                      <Skeleton variant="rectangular" width={180} height={41} sx={{ borderRadius: 1 }} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : activeConfig ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Store Name
                  </Typography>
                  <Typography variant="body1" gutterBottom component="div">
                    {activeConfig.storeName || activeConfig.store_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Store URL
                  </Typography>
                  <Typography variant="body1" gutterBottom component="div">
                    {activeConfig.storeUrl || activeConfig.store_url || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    BSS B2B API URL
                  </Typography>
                  <Typography variant="body1" gutterBottom component="div">
                    {activeConfig.bssB2bApiUrl || activeConfig.bss_b2b_api_url || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Created At
                  </Typography>
                  <Typography variant="body1" gutterBottom component="div">
                    {formatDate(activeConfig.createdAt || activeConfig.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Updated At
                  </Typography>
                  <Typography variant="body1" gutterBottom component="div">
                    {formatDate(activeConfig.updatedAt || activeConfig.updated_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" component="div">
                    Status
                  </Typography>
                  <Typography variant="body1" gutterBottom component="div">
                    <StatusBadge
                      customize={true}
                      customLabel={activeConfig.isActive ? 'Active' : 'Inactive'}
                      customColor={'success'}
                    />
                  </Typography>
                </Grid>
                {isAdmin && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', sm: 'stretch', md: 'center' }
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={() => handleTestConnection(activeConfig.id)}
                        disabled={testingConnectionId !== null || setActiveMutation.isPending || deactivateMutation.isPending}
                        sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                      >
                        {testingConnectionId === activeConfig.id ? 'Testing...' : 'Test Connection'}
                      </Button>

                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(activeConfig)}
                        disabled={testingConnectionId !== null || setActiveMutation.isPending || deactivateMutation.isPending}
                        sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SettingsIcon />}
                        onClick={() => setIsWebhookModalOpen(true)}
                        disabled={testingConnectionId !== null || setActiveMutation.isPending || deactivateMutation.isPending}
                        sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                      >
                        Manage Webhooks
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" component="div">
                No active configuration found.
              </Typography>
              {isAdmin && (
                allConfigs?.data && allConfigs?.data?.length > 0 ? (
                  <Typography variant="body1" color="text.secondary" component="div" sx={{ mt: 2 }}>
                    Please activate below configuration to start syncing data.
                  </Typography>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={testingConnectionId !== null || setActiveMutation.isPending || deactivateMutation.isPending}
                    sx={{ mt: 2 }}
                  >
                    Create Now
                  </Button>
                )
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* All Configurations (Admin Only) */}
      {isAdmin && (
        <>
          <ServerDataTable
            data={allConfigs?.data || []}
            columns={columns}
            totalRows={allConfigs?.pagination?.total || 0}
            currentPage={pageIndex}
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            loading={isLoadingAll}
            loadingRows={loadingRows}
            noDataMessage="No configurations found."
            totalPages={allConfigs?.pagination?.totalPages || 0}
          />
        </>
      )}

      {/* Configuration Form Dialog */}
      {isAdmin && (
        <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ py: 2.5, px: 3, fontSize: 18 }}>
            {selectedConfig ? 'Edit Shopify Configuration' : 'Add Shopify Configuration'}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={isMobile ? 1 : 2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="storeName"
                  name="storeName"
                  label="Store Name (Optional)"
                  value={formik.values.storeName || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.storeName && Boolean(formik.errors.storeName)}
                  helperText={formik.touched.storeName && formik.errors.storeName}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="storeUrl"
                  name="storeUrl"
                  label="Store URL"
                  placeholder="your-store.myshopify.com"
                  value={formik.values.storeUrl || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.storeUrl && Boolean(formik.errors.storeUrl)}
                  helperText={formik.touched.storeUrl && formik.errors.storeUrl}
                  variant="outlined"
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: 'new-password' // inputProps autocomplete
                  }}
                  sx={{ mb: 2 }}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="accessToken"
                  name="accessToken"
                  label="Access Token"
                  type={showAccessToken ? 'text' : 'password'}
                  value={formik.values.accessToken || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.accessToken && Boolean(formik.errors.accessToken)}
                  helperText={
                    (formik.touched.accessToken && formik.errors.accessToken) ||
                    (selectedConfig ? 'Leave blank to keep current token' : 'This value will be stored in encoded format')
                  }
                  variant="outlined"
                  sx={{ mb: 2 }}
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: 'new-password' // inputProps autocomplete
                  }}
                  required={!selectedConfig}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle access token visibility"
                          onClick={() => setShowAccessToken(!showAccessToken)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={!isAdmin}
                        >
                          {showAccessToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="webhookSecret"
                  name="webhookSecret"
                  label='Webhook Secret'
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={formik.values.webhookSecret || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.webhookSecret && Boolean(formik.errors.webhookSecret)}
                  helperText={
                    (formik.touched.webhookSecret && formik.errors.webhookSecret) ||
                    (selectedConfig ? 'Leave blank to keep current secret' : 'This value will be stored in encoded format')
                  }
                  required={!selectedConfig}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  autoComplete="off"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle webhook secret visibility"
                          onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={!isAdmin}
                        >
                          {showWebhookSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="bssB2bApiUrl"
                  name="bssB2bApiUrl"
                  label='BSS B2B API URL'
                  value={formik.values.bssB2bApiUrl || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.bssB2bApiUrl && Boolean(formik.errors.bssB2bApiUrl)}
                  helperText={
                    (formik.touched.bssB2bApiUrl && formik.errors.bssB2bApiUrl)
                  }
                  variant="outlined"
                  sx={{ mb: 2 }}
                  placeholder="https://example.com/api/v1"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="bssB2bAccessKey"
                  name="bssB2bAccessKey"
                  label='BSS B2B Access Key'
                  type={showBssB2bAccessKey ? 'text' : 'password'}
                  value={formik.values.bssB2bAccessKey || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.bssB2bAccessKey && Boolean(formik.errors.bssB2bAccessKey)}
                  helperText={
                    (formik.touched.bssB2bAccessKey && formik.errors.bssB2bAccessKey) ||
                    (selectedConfig ? 'Leave blank to keep current key' : 'This value will be stored in encoded format')
                  }
                  variant="outlined"
                  sx={{ mb: 2 }}
                  autoComplete="off"
                  placeholder={selectedConfig ? 'Access Key' : ''}
                  required={!selectedConfig}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle BSS B2B access key visibility"
                          onClick={() => setShowBssB2bAccessKey(!showBssB2bAccessKey)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={!isAdmin}
                        >
                          {showBssB2bAccessKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleDialogClose} variant="contained" color="secondary" disabled={createMutation.isPending || updateMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={formik.handleSubmit}
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending || !hasFormChanges || !formik.isValid}
            >
              {selectedConfig ? (
                updateMutation.isPending ? 'Updating...' : 'Update'
              ) : (
                createMutation.isPending ? 'Creating...' : 'Create'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {isAdmin && (
        <ConfirmDialog
          open={isDeleteDialogOpen}
          title="Delete Shopify Configuration"
          message={`Are you sure you want to delete the Shopify configuration for ${selectedConfig?.storeName || selectedConfig?.store_name || 'this store'}?`}
          confirmColor="error"
          onConfirm={() => deleteMutation.mutate(selectedConfig?.id)}
          onCancel={() => setIsDeleteDialogOpen(false)}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Deactivate Confirmation Dialog */}
      {isAdmin && (
        <ConfirmDialog
          open={isDeactivateDialogOpen}
          title="Deactivate Shopify Configuration"
          message={`Are you sure you want to deactivate the Shopify configuration for ${selectedConfig?.storeName || selectedConfig?.store_name || 'this store'}? This will disable all synchronization features until another configuration is activated.`}
          confirmColor="warning"
          onConfirm={() => deactivateMutation.mutate(selectedConfig?.id)}
          onCancel={() => {
            setIsDeactivateDialogOpen(false);
            setSelectedConfig(null);
          }}
          isLoading={deactivateMutation.isPending}
          confirmText="Deactivate"
        />
      )}

      {/* Webhook Modal */}
      <WebhookModal
        open={isWebhookModalOpen}
        onClose={() => setIsWebhookModalOpen(false)}
        activeConfig={activeConfig}
        webhookResult={webhookResult}
        setWebhookResult={setWebhookResult}
        showWebhookResult={showWebhookResult}
        setShowWebhookResult={setShowWebhookResult}
      />
    </Box>
  );
}
